import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FromStore } from "../../../helpers/Store";
import { Box, IconButton, Paper, styled, Table, TableBody, TableHead, TableRow, useMediaQuery, useTheme } from "@mui/material";
import FloatingActionButton from "../../components/FloatingActionButton/FloatingActionButton";
import Navigation from "../../components/Navigation/Navigation";
import DeleteIcon from '@mui/icons-material/Delete';
import Button from "../../../PublicSide/components/Button/Button";
import AddServerDialog from "./AddServerDialog";
import DeleteConfirmationDialog from "../../components/DeleteConfirmation/DeleteConfirmationDialog";
import { CenteredContainer, StyledTableCellServerVersionPage, StyledTableContainerServersUsersManagement } from "../../../style/GlobalsComponentsStyled";
import BackButtonWithTitle from "../../components/BackButtonWithTitle/BackButtonWithTitle";
import { HandleError, HandleSuccess, ShowErrorMessage } from '../../../stores/page/actions';
import ServiceLocator from "../../../services/ServiceLocator";
import { ApiGetServers, ServerActionUpdated, ServerAdded, ServerDeployed, ServerRemoved, ServersActionUpdated } from "../../../stores/api/server/actions";
import { WithRefreshToken } from "../../../stores/auth/actions";
import { useAppDispatch } from "../../../helpers/UseAppDispatch";
import { ServerData } from "../../../api/dtos/server/ServerResponse";
import { ServerAction } from "../../../api/dtos/server/ServerAction";
import { ServerActionRequest } from "../../../api/dtos/server/ServerActionRequest";

const StyledBoxButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    gap: '1.5rem',
    [theme.breakpoints.up('md')]: {
        maxWidth: '75%',
    },
    [theme.breakpoints.between('md', 1100)]: {
        maxWidth: '90%',
    },
}));

const ServersPage: React.FC = () => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const dispatch = useAppDispatch();
    const servers: ServerData[] = FromStore(state => state.apis.servers.servers);
    const [isAddServerDialogOpen, setIsAddServerDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [serverToDelete, setServerToDelete] = useState<ServerData | null>(null);
    const Navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    useEffect(() => {
    }, [servers]);

    const getAllServers = async () => {
        await dispatch(ApiGetServers(Number(applicationId)));
    };

    useEffect(() => {
        getAllServers();
    }, [])

    const handleRemoveServer = async (serverId: number) => {
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ServerService.RemoveServer(Number(applicationId), serverId);
            if (response.is_success) {
                dispatch(ServerRemoved(serverId));
                setIsDeleteDialogOpen(false);
                dispatch(HandleSuccess('Server removed successfully.'));
                getAllServers();
            } else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleAddServer = async (applicationId: number, domainName: string, runnerName: string, ip: string, sshUser: string, sshPrivateKey: string, deployScript: string, comments: string) => {
        const request = { domainName, runnerName, ip, sshUser, sshPrivateKey, deployScript, comments }
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ServerService.AddServer(applicationId, request);
            if (response.is_success) {
                dispatch(ServerAdded(response.data));
                setIsAddServerDialogOpen(false);
                getAllServers();
                dispatch(HandleSuccess('Server added successfully.'));
            } else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleDeployServer = async (serverDomain: string, serverId: number) => {
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ServerService.DeployServer(Number(applicationId), serverId);
            if (response.is_success) {
                dispatch(ServerDeployed());
                getAllServers();
                dispatch(HandleSuccess(`Server ${serverDomain} deployed successfully.`));
            } else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleToggleServer = async (serverDomain: string, serverId: number, isStarted: boolean, request: ServerActionRequest) => {
        const action = isStarted ? ServerAction.Stop : ServerAction.Start;
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ServerService.UpdateServerAction(Number(applicationId), serverId, { action });
            if (response.is_success) {
                dispatch(ServerActionUpdated(serverId, request));
                dispatch(HandleSuccess(`Server ${serverDomain} ${isStarted ? "stopped" : "started"} successfully.`));
            } else {
                dispatch(ShowErrorMessage(`Failed to ${isStarted ? "stop" : "start"} server ${serverId}.`));
            }
        }, dispatch);
    };

    const handleToggleAllServers = async () => {
        const allStarted = servers.every(server => server.isStarted);
        const action = allStarted ? ServerAction.Stop : ServerAction.Start;
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ServerService.UpdateServersAction(Number(applicationId), { action });
            if (response.is_success) {
                dispatch(ServersActionUpdated({ action }));
                dispatch(HandleSuccess(`All servers ${allStarted ? "stopped" : "started"} successfully.`));
            } else {
                dispatch(ShowErrorMessage(`Failed to ${servers.every(s => s.isStarted) ? "stop" : "start"} all servers.`));
            }
        }, dispatch);
    };

    return (
        <>
            <Navigation />
            <CenteredContainer className="main_content">
                <BackButtonWithTitle title="Manage Servers" />
                <>
                    <StyledBoxButton>
                        <Button text="Version History" onClick={() => Navigate(`/applications/${applicationId}/versions`)} style={{ width: 'auto' }} />
                        <Button text={servers.every(s => s.isStarted) ? "Stop All Servers" : "Start All Servers"} onClick={handleToggleAllServers} style={{ width: 'auto' }} />
                    </StyledBoxButton>
                    <StyledTableContainerServersUsersManagement>
                        <Paper sx={{ backgroundColor: '#1C1D2B', display: "table", width: "100%" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCellServerVersionPage>Domain</StyledTableCellServerVersionPage>
                                        <StyledTableCellServerVersionPage>Runner</StyledTableCellServerVersionPage>
                                        <StyledTableCellServerVersionPage>SSH User</StyledTableCellServerVersionPage>
                                        <StyledTableCellServerVersionPage>Deploy</StyledTableCellServerVersionPage>
                                        <StyledTableCellServerVersionPage>Action</StyledTableCellServerVersionPage>
                                        <StyledTableCellServerVersionPage>Delete</StyledTableCellServerVersionPage>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {servers.map((server: ServerData) => (
                                        <TableRow key={server.id}>
                                            <StyledTableCellServerVersionPage>{server.domainName}</StyledTableCellServerVersionPage>
                                            <StyledTableCellServerVersionPage>{server.runnerName}</StyledTableCellServerVersionPage>
                                            <StyledTableCellServerVersionPage>{server.sshUser}</StyledTableCellServerVersionPage>
                                            <StyledTableCellServerVersionPage>
                                                <Button text="Deploy" onClick={() => handleDeployServer(server.domainName, server.id)} style={{ width: isDesktop ? '80%' : '100%' }} />
                                            </StyledTableCellServerVersionPage>
                                            <StyledTableCellServerVersionPage>
                                                <Button text={server.isStarted ? "Stop" : "Start"} onClick={() => handleToggleServer(server.domainName, server.id, server.isStarted, { action: server.isStarted ? ServerAction.Stop : ServerAction.Start })} style={{ width: isDesktop ? '80%' : '100%' }} />
                                            </StyledTableCellServerVersionPage>
                                            <StyledTableCellServerVersionPage>
                                                <IconButton onClick={() => { setServerToDelete(server); setIsDeleteDialogOpen(true); }} color="error" aria-label='delete server'>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </StyledTableCellServerVersionPage>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </StyledTableContainerServersUsersManagement>
                </>
                <FloatingActionButton onClick={() => setIsAddServerDialogOpen(true)} label="Add Server" />

                <AddServerDialog
                    open={isAddServerDialogOpen}
                    onClose={() => setIsAddServerDialogOpen(false)}
                    onSubmit={handleAddServer}
                    applicationId={Number(applicationId)}
                />
                <DeleteConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={() => handleRemoveServer(serverToDelete?.id as number)}
                />
            </CenteredContainer>
        </>
    );
};

export default ServersPage;
